import React, { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { isMobile } from 'react-device-detect';
import { AboutContext } from '../../../context/aboutContext';
import { GeneralContext } from '../../../context/generalContext';
import { addressesAPI } from '../../../services/GeneralService';
import classes from './AboutContacts.module.scss';
import '../../../styles/Contacts.css';

export default function AboutContacts() {
  const { setActiveSection } = useContext(AboutContext);
  const { path, activeLanguage, activeCity, cities, currentCityAddress, subdomainIsActive, selectedAddress } =
    useContext(GeneralContext);
  const { data: addresses } = addressesAPI.useFetchAllAddressesQuery();

  // при изменении path проверяем в каком разделе находимся в данный момент и устанавливаем активный раздел
  useEffect(() => {
    if (path.slice(-8) === 'contacts') {
      setActiveSection(4);
    }
  }, [path]);

  const mapRef = React.useRef();

  const handleLoad = () => {
    mapRef.current.innerHTML = '';
    if (addresses) {
      window.ymaps.ready(() => {
        let localMap;
        if (activeCity === 1) {
          localMap = new window.ymaps.Map(
            'map',
            {
              center: [55.80496108, 37.58991815],
              zoom: 15,
            },
            {
              yandexMapDisablePoiInteractivity: true, // отключаем точки интереса
            },
          );
        } else if (activeCity === 3) {
          localMap = new window.ymaps.Map(
            'map',
            {
              center: currentCityAddress?.geo,
              zoom: 17,
            },
            {
              yandexMapDisablePoiInteractivity: true, // отключаем точки интереса
            },
          );
        } else {
          localMap = new window.ymaps.Map(
            'map',
            {
              center: currentCityAddress?.geo,
              zoom: 15,
            },
            {
              yandexMapDisablePoiInteractivity: true, // отключаем точки интереса
            },
          );
        }

        let placemark1;
        if (activeCity === 1) {
          placemark1 = new window.ymaps.Placemark(
            [55.80496108, 37.58991815],
            {},
            {
              iconLayout: 'default#image',
              iconImageHref: subdomainIsActive
                ? addresses?.filter(address => address.id === selectedAddress)[0]?.map_pin || '/images/map-pin.png'
                : '/images/map-pin.png',
              iconImageSize: [45, 51],
              iconImageOffset: [-22, -51],
            },
          );
        } else {
          placemark1 = new window.ymaps.Placemark(
            currentCityAddress?.geo,
            {},
            {
              iconLayout: 'default#image',
              iconImageHref: subdomainIsActive
                ? addresses?.filter(address => address.id === selectedAddress)[0]?.map_pin || '/images/map-pin.png'
                : '/images/map-pin.png',
              iconImageSize: [45, 51],
              iconImageOffset: [-22, -51],
            },
          );
        }
        localMap.controls.remove('geolocationControl'); // удаляем геолокацию
        localMap.controls.remove('searchControl'); // удаляем поиск
        localMap.controls.remove('trafficControl'); // удаляем контроль трафика
        localMap.controls.remove('typeSelector'); // удаляем тип
        localMap.controls.remove('fullscreenControl'); // удаляем кнопку перехода в полноэкранный режим
        localMap.controls.remove('zoomControl'); // удаляем контрол зуммирования
        localMap.controls.remove('rulerControl'); // удаляем контрол правил
        localMap.geoObjects.add(placemark1);

        localMap.behaviors.disable(['scrollZoom']); // отключаем скролл карты (опционально)
        const eventsPane = localMap.panes.get('events');
        const eventsPaneEl = eventsPane.getElement();

        // отключаем для мобильной версии drag + в стилях для ymaps указываем "touch-action: auto" = это позволяет скролллить содержимое карты только двумя пальцами, а не одним
        isMobile ? localMap.behaviors.disable('drag') : localMap.behaviors.enable('drag');
        if (isMobile) {
          localMap.behaviors.disable('drag');
          window.ymaps.domEvent.manager.add(eventsPaneEl, 'touchmove', event => {
            if (event.get('touches').length === 1) {
              eventsPaneEl.style.transition = 'opacity .3s';
              eventsPaneEl.style.background = 'rgba(0, 0, 0, .45)';
              eventsPaneEl.style.alignItems = 'center';
              eventsPaneEl.style.display = 'flex';
              eventsPaneEl.style.justifyContent = 'center';
              eventsPaneEl.style.alignItems = 'center';
              eventsPaneEl.style.fontSize = '18px';
              eventsPaneEl.style.fontFamily = 'Open Sans, sans-serif';
              eventsPaneEl.style.textAlign = 'center';
              eventsPaneEl.style.color = 'white';
              eventsPaneEl.textContent =
                window.location.pathname.slice(1, 3) === 'en'
                  ? 'Use two fingers to move the map'
                  : 'Чтобы переместить карту, проведите по ней двумя пальцами';
              eventsPaneEl.style.opacity = '1';
            }
          });
          window.ymaps.domEvent.manager.add(eventsPaneEl, 'touchend', () => {
            eventsPaneEl.style.transition = 'opacity .8s';
            eventsPaneEl.style.opacity = '0';
          });
        }
      });
    }
  };

  useEffect(() => {
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  });

  useEffect(() => {
    mapRef.current.innerHTML = '';
    handleLoad();
  }, [activeCity, addresses, currentCityAddress]);

  return (
    <div className={classes.contactsDiv}>
      <Helmet>
        <title>{activeLanguage === 1 ? 'Контакты | Brasserie Lambic' : 'Contacts | Brasserie Lambic'}</title>
      </Helmet>
      <div id='map' className={classes.map} ref={mapRef} />

      <div className={classes.info}>
        <div className={classes.title}>
          {activeLanguage === 1
            ? 'Отвечаем с 10 до 18 (кроме выходных)'
            : 'We answer from 10.00 till 18.00 (except weekends)'}
        </div>
        <div itemScope itemType='https://schema.org/LocalBusiness'>
          <div itemProp='address' itemScope itemType='https://schema.org/PostalAddress'>
            {activeCity === 1 && (
              <div className={classes.address} itemProp='streetAddress'>
                {activeLanguage === 1
                  ? '127015, Москва, ул. Новодмитровская 2к1'
                  : '127015, Moscow, Novodmitrovskaya st., house 2 building 1'}
              </div>
            )}
            {activeCity !== 1 && currentCityAddress && (
              <div
                itemProp='streetAddress'
                className={classes.address}
                dangerouslySetInnerHTML={{
                  __html:
                    activeLanguage === 1
                      ? currentCityAddress?.description
                      : currentCityAddress?.description_en || currentCityAddress?.description,
                }}
              />
            )}
          </div>
          <div className={classes.tel}>
            {currentCityAddress?.tel && (
              <a href={`tel:${currentCityAddress.tel}`}>
                <span itemProp='telephone'>{currentCityAddress.telModified}</span>
              </a>
            )}
          </div>
          <div className={classes.mail}>
            <a href={`mailto:${cities.filter(city => city.id === activeCity)[0]?.email}`}>
              <span itemProp='email'>{cities.filter(city => city.id === activeCity)[0]?.email || ''}</span>
            </a>
          </div>
        </div>
        <div className={classes.rent}>
          <div className={classes.title}>{activeLanguage === 1 ? 'Аренда' : 'Rent'}</div>
          <a href='mailto:arenda@lambic.ru'>arenda@lambic.ru</a>
        </div>
        <div className={classes.hr}>
          <div className={classes.title}>{activeLanguage === 1 ? 'Трудоустройство' : 'Employment'}</div>
          <a href='mailto:hr@upstergroup.ru'>hr@upstergroup.ru</a>
        </div>
      </div>
    </div>
  );
}
